import { useState, useEffect, useMemo, useCallback } from "react";
import { db } from "../../../../firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  writeBatch,
  setDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import AddUserModal from "../AddUserModal";
import SearchBar from "../SearchBar";
import ModuleCard from "../ModuleCard";
import "./UserSection.css";
import RemoveUserModal from "../RemoveUserModal";
import {
  extractDomainFromEmail,
  getAllUsersFromEnterprises,
  getDomainsBySubsidiaries,
  getUserBySubsidiaries,
  getUserDocument,
  updateUserField,
} from "../../../../utils/userHelpers";
import {
  categorizeModuleUsers,
  countActiveUsersBySubsidiaries,
} from "../../../../utils/moduleUtils";

function UsersSection({ userRole, modulesData, userInformations }) {
  const [usersData, setUsersData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [loadingModules, setLoadingModules] = useState({});
  const [permittedDomains, setPermittedDomains] = useState([]);
  const [subsidiariesUsers, setSubsidiariesUsers] = useState([]);
  const [subsidiaryUserCounts, setSubsidiaryUserCounts] = useState({});
  const [availableSubsidiaries, setAvailableSubsidiaries] = useState([]);
  const [moduleAllowedUsersOverride, setModuleAllowedUsersOverride] = useState({});

  // Função para ativar o carregamento de todos os módulos
  const setAllModulesLoading = (isLoading) => {
    const loadingState = modulesData.reduce((acc, module) => {
      acc[module.id] = isLoading; // Define o estado de carregamento para cada módulo
      return acc;
    }, {});

    setLoadingModules(loadingState); // Atualiza o estado com todos os módulos
  };

  // Load all users from Firestore if the 'users' field exists in the documents
  const loadUsersData = useCallback(async () => {
    setAllModulesLoading(true);
    try {
      const allUsersList = await getAllUsersFromEnterprises({
        includeDomain: false,
        sorted: true,
      });

      setUsersData(allUsersList);
    } catch (error) {
      console.warn("[loadUsersData] Erro carregando dados de usuario: ", error);
    } finally {
      setAllModulesLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadSubsidiaryCounts = async () => {
      const allSubsidiaries = new Set();
      modulesData.forEach((module) => {
        (module.subsidiaries || []).forEach((sub) => allSubsidiaries.add(sub));
      });

      const counts = await countActiveUsersBySubsidiaries([...allSubsidiaries]);
      setSubsidiaryUserCounts(counts);
    };

    loadSubsidiaryCounts();

    // Reseta o override quando modulesData é atualizado do servidor
    setModuleAllowedUsersOverride({});
  }, [modulesData]);

  // Carrega os dados dos usuários na montagem do componente
  useEffect(() => {
    loadUsersData();
  }, [loadUsersData]);

  useEffect(() => {
    const getDomains = async () => {
      const domains = await getDomainsBySubsidiaries(
        userInformations.subsidiaries
      );
      setPermittedDomains(domains);
    };

    getDomains();
  }, [userInformations.subsidiaries]);

  useEffect(() => {
    const loadUsers = async () => {
      const users = await getUserBySubsidiaries(userInformations.subsidiaries);
      setSubsidiariesUsers(users);
    };

    loadUsers();
  }, [userInformations.subsidiaries]);

  // Carrega lista de subsidiárias disponíveis
  useEffect(() => {
    const loadSubsidiaries = async () => {
      try {
        const subsidiaryRef = collection(db, "subsidiary");
        const snapshot = await getDocs(subsidiaryRef);

        const subs = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));

        setAvailableSubsidiaries(subs);
      } catch (error) {
        console.warn("[loadSubsidiaries] Erro:", error);
      }
    };

    loadSubsidiaries();
  }, []);

  // Carrega contadores de usuários por subsidiária
  useEffect(() => {
    const loadSubsidiaryCounts = async () => {
      try {
        const allSubsidiaries = new Set();
        modulesData.forEach((module) => {
          (module.subsidiaries || []).forEach((sub) =>
            allSubsidiaries.add(sub)
          );
        });

        if (allSubsidiaries.size > 0) {
          const counts = await countActiveUsersBySubsidiaries([...allSubsidiaries]);
          setSubsidiaryUserCounts(counts);
        }
      } catch (error) {
        console.warn("[loadSubsidiaryCounts] Erro:", error);
      }
    };

    loadSubsidiaryCounts();
  }, [modulesData]);

  const filteredUsersForModule = (moduleId) => {
    // Busca o módulo para verificar allowedUsers
    const module = modulesData.find((m) => m.id === moduleId);
    const allowedUsers = module?.allowedUsers || [];

    return (
      usersData
        // Filtrar usuários que possuem o módulo acessível
        .filter(
          (user) =>
            user.accessibleModules &&
            user.accessibleModules.some((module) => module.id === moduleId)
        )
        // Filtrar usuários que pertencem às subsidiárias permitidas
        // OU que foram adicionados manualmente (allowedUsers)
        .filter((user) => {
          if (userRole === "admin_global") {
            return true;
          }

          // Para admin_local, verificar se o usuário tem subsidiária em comum
          if (userRole === "admin_local" && userInformations.subsidiaries?.length > 0) {
            const hasCommonSubsidiary = user.subsidiaries?.some((userSub) =>
              userInformations.subsidiaries.includes(userSub)
            );

            if (!hasCommonSubsidiary) {
              return false;
            }
          }

          // Se usuário foi adicionado manualmente, mostrar (já passou pelo filtro de subsidiária acima)
          if (allowedUsers.includes(user.email)) {
            return true;
          }

          // Caso contrário, verificar subsidiárias por domínio
          const userDomain = extractDomainFromEmail(user.email);
          const isInPermittedDomains = permittedDomains.includes(userDomain);
          const isInSubsidiariesUsers = subsidiariesUsers.some(
            (subsidiaryUser) => subsidiaryUser.email === user.email
          );

          return isInPermittedDomains || isInSubsidiariesUsers;
        })
        // Filtrar com base no termo de pesquisa
        .filter(
          (user) =>
            (user.name &&
              user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.email &&
              user.email.toLowerCase().includes(searchTerm.toLowerCase()))
        )
    );
  };

  const getModuleUsersData = useCallback(
    (moduleId) => {
      const module = modulesData.find((m) => m.id === moduleId);
      if (!module) {
        return {
          manualUsers: [],
          subsidiaries: [],
          manualUserCount: 0,
        };
      }

      const allUsers = filteredUsersForModule(moduleId);

      // Usa o override local se existir, caso contrário usa os dados do módulo
      const moduleWithOverride = {
        ...module,
        allowedUsers: moduleAllowedUsersOverride[moduleId] ?? module.allowedUsers,
      };

      const { manualUsers, subsidiaries } = categorizeModuleUsers(
        allUsers,
        moduleWithOverride
      );

      const subsidiariesWithCounts = subsidiaries.map((subId) => ({
        id: subId,
        name: availableSubsidiaries.find((s) => s.id === subId)?.name || subId,
        userCount: subsidiaryUserCounts[subId] || 0,
      }));

      return {
        manualUsers,
        subsidiaries: subsidiariesWithCounts,
        manualUserCount: manualUsers.length,
      };
    },
    [
      modulesData,
      usersData,
      searchTerm,
      permittedDomains,
      subsidiariesUsers,
      availableSubsidiaries,
      subsidiaryUserCounts,
      moduleAllowedUsersOverride,
    ]
  );

  // Remove um usuário de um módulo
  const removeUserFromModule = async (userEmail, moduleId) => {
    try {
      setLoadingModules((prev) => ({ ...prev, [moduleId]: true }));

      const userDoc = await getUserDocument(userEmail);

      if (!userDoc) {
        console.warn(`Usuário ${userEmail} não encontrado`);
        return;
      }

      const { docRef, usersMap } = userDoc;

      const updatedModules = usersMap[userEmail].accessibleModules.filter(
        (module) => module.id !== moduleId
      );

      usersMap[userEmail].accessibleModules = updatedModules;

      await updateDoc(docRef, { users: usersMap });

      // Remove do allowedUsers do módulo
      const moduleDocRef = doc(db, "modules", moduleId);
      await updateDoc(moduleDocRef, {
        allowedUsers: arrayRemove(userEmail),
      });

      // Atualiza o override local para refletir a remoção imediatamente
      setModuleAllowedUsersOverride((prev) => {
        const module = modulesData.find((m) => m.id === moduleId);
        const currentAllowedUsers = prev[moduleId] ?? module?.allowedUsers ?? [];
        return {
          ...prev,
          [moduleId]: currentAllowedUsers.filter((email) => email !== userEmail),
        };
      });

      setUsersData((prevUsersData) =>
        prevUsersData.map((user) =>
          user.email === userEmail
            ? { ...user, accessibleModules: updatedModules }
            : user
        )
      );
    } catch (error) {
      console.warn("[removeUserFromModule] Erro: ", error);
    } finally {
      setLoadingModules((prev) => ({ ...prev, [moduleId]: false }));
    }
  };

  // Abre o modal de remoção de usuários
  const handleRemoveUser = (module) => {
    setSelectedModule(module);
    setIsRemovingUser(true);
  };

  const handleRemoveUsersFromModule = async (selectedUsers, moduleRef) => {
    try {
      setLoadingModules((prev) => ({ ...prev, [moduleRef.id]: true }));
      setIsRemovingUser(false); // Fecha o modal de remoção

      // Cria um batch para agrupar todas as atualizações
      const batch = writeBatch(db);

      // Objeto para armazenar os dados de usuários carregados por domínio
      const userDomainsData = {};

      // Carregar todos os documentos regional_enterprises de uma vez
      const enterprisesSnapshot = await getDocs(
        collection(db, "regional_enterprises")
      );
      enterprisesSnapshot.forEach((docSnapshot) => {
        userDomainsData[docSnapshot.id] = docSnapshot.data().users || {};
      });

      // Carregar o documento other_users uma vez
      const otherUsersRef = doc(db, "regional_enterprises", "other_users");
      const otherUsersDoc = await getDoc(otherUsersRef);
      const otherUsersData = otherUsersDoc.exists()
        ? otherUsersDoc.data().users
        : {};

      // Objeto para armazenar as atualizações para cada domínio
      const updatedUsersMap = {};
      let otherUsersUpdated = false; // Controle para identificar alterações em other_users

      // Processa cada usuário selecionado para remoção
      for (const email of selectedUsers) {
        const userDomain = extractDomainFromEmail(email);

        // Obter os dados do usuário do domínio correspondente ou de other_users
        const usersMap = userDomainsData[userDomain] || otherUsersData;

        if (!usersMap[email]) {
          continue; // Pula para o próximo usuário se não encontrado
        }

        // Atualizar `other_users` separadamente
        if (userDomainsData[userDomain]) {
          // Inicializa o mapa atualizado para o domínio se necessário
          if (!updatedUsersMap[userDomain]) {
            updatedUsersMap[userDomain] = { ...usersMap };
          }

          // Filtra o módulo a ser removido do usuário
          updatedUsersMap[userDomain][email].accessibleModules =
            updatedUsersMap[userDomain][email].accessibleModules.filter(
              (module) => module.id !== moduleRef.id
            );
        } else {
          // Atualizar diretamente em `other_users`
          otherUsersData[email].accessibleModules = otherUsersData[
            email
          ].accessibleModules.filter((module) => module.id !== moduleRef.id);
          otherUsersUpdated = true; // Indica que houve mudanças em `other_users`
        }
      }

      // Adiciona todas as operações de atualização ao batch
      for (const [userDomain, usersMap] of Object.entries(updatedUsersMap)) {
        const userDomainRef = doc(db, "regional_enterprises", userDomain);
        batch.update(userDomainRef, { users: usersMap }); // Atualiza o documento do domínio
      }

      // Se houve alterações em `other_users`, adiciona ao batch
      if (otherUsersUpdated) {
        batch.update(otherUsersRef, { users: otherUsersData });
      }

      // Commit de todas as operações no batch
      await batch.commit();

      const moduleDocRef = doc(db, "modules", moduleRef.id);
      await updateDoc(moduleDocRef, {
        allowedUsers: arrayRemove(...selectedUsers),
      });

      // Atualiza o override local para refletir a remoção imediatamente
      setModuleAllowedUsersOverride((prev) => {
        const module = modulesData.find((m) => m.id === moduleRef.id);
        const currentAllowedUsers = prev[moduleRef.id] ?? module?.allowedUsers ?? [];
        return {
          ...prev,
          [moduleRef.id]: currentAllowedUsers.filter(
            (email) => !selectedUsers.includes(email)
          ),
        };
      });

      // Atualiza o estado de usersData localmente para refletir as mudanças
      setUsersData((prevUsersData) =>
        prevUsersData.map((user) =>
          selectedUsers.includes(user.email)
            ? {
                ...user,
                accessibleModules: user.accessibleModules.filter(
                  (module) => module.id !== moduleRef.id
                ),
              }
            : user
        )
      );
    } catch (error) {
      console.warn(
        "[handleRemoveUsersFromModule] Erro removendo usuarios do modulo: ",
        error
      );
    } finally {
      setLoadingModules((prev) => ({ ...prev, [moduleRef.id]: false }));
    }
  };

  // Manipula a adição de usuário ao módulo
  const handleAddUser = (module) => {
    setSelectedModule(module);
    setIsAddingUser(true);
  };

  const handleSaveUsersToModule = async (selectedUsers, moduleRef) => {
    try {
      setLoadingModules((prev) => ({ ...prev, [moduleRef.id]: true }));
      setIsAddingUser(false); // Fecha o modal

      const batch = writeBatch(db);
      const userDomainsData = {};

      // Carregar todos os documentos regional_enterprises de uma vez
      const enterprisesSnapshot = await getDocs(
        collection(db, "regional_enterprises")
      );
      enterprisesSnapshot.forEach((docSnapshot) => {
        userDomainsData[docSnapshot.id] = docSnapshot.data().users || {};
      });

      // Carregar o documento other_users uma vez
      const otherUsersRef = doc(db, "regional_enterprises", "other_users");
      const otherUsersDoc = await getDoc(otherUsersRef);
      const otherUsersData = otherUsersDoc.exists()
        ? otherUsersDoc.data().users
        : {};

      // Objeto para armazenar as atualizações para cada domínio
      const updatedUsersMap = {};
      let otherUsersUpdated = false;

      for (const email of selectedUsers) {
        const userDomain = extractDomainFromEmail(email);
        const usersMap = userDomainsData[userDomain] || otherUsersData;

        if (!usersMap[email]) {
          continue; // Pula para o próximo usuário
        }

        // Atualizar `regional_enterprises` ou `other_users` conforme necessário
        if (userDomainsData[userDomain]) {
          // Inicializa o mapa atualizado para o domínio se necessário
          if (!updatedUsersMap[userDomain]) {
            updatedUsersMap[userDomain] = { ...usersMap };
          }

          if (
            !updatedUsersMap[userDomain][email].accessibleModules.some(
              (module) => module.id === moduleRef.id
            )
          ) {
            updatedUsersMap[userDomain][email].accessibleModules.push(
              moduleRef
            );
          }
        } else {
          // Atualizar diretamente em `other_users`
          if (
            !otherUsersData[email].accessibleModules.some(
              (module) => module.id === moduleRef.id
            )
          ) {
            otherUsersData[email].accessibleModules.push(moduleRef); // Adiciona o módulo
            otherUsersUpdated = true; // Indica que houve mudanças em `other_users`
          }
        }
      }

      // Executa todas as atualizações em batch
      for (const [userDomain, usersMap] of Object.entries(updatedUsersMap)) {
        const userDomainRef = doc(db, "regional_enterprises", userDomain);
        batch.update(userDomainRef, { users: usersMap });
      }

      // Se houve alterações em `other_users`, adiciona ao batch
      if (otherUsersUpdated) {
        batch.update(otherUsersRef, { users: otherUsersData });
      }

      await batch.commit();

      const moduleDocRef = doc(db, "modules", moduleRef.id);
      await updateDoc(moduleDocRef, {
        allowedUsers: arrayUnion(...selectedUsers),
      });

      // Atualiza o override local para refletir a adição imediatamente
      setModuleAllowedUsersOverride((prev) => {
        const module = modulesData.find((m) => m.id === moduleRef.id);
        const currentAllowedUsers = prev[moduleRef.id] ?? module?.allowedUsers ?? [];
        const newAllowedUsers = [...new Set([...currentAllowedUsers, ...selectedUsers])];
        return {
          ...prev,
          [moduleRef.id]: newAllowedUsers,
        };
      });

      setUsersData((prevUsersData) =>
        prevUsersData.map((user) => {
          if (selectedUsers.includes(user.email)) {
            // Verifica se o módulo já existe antes de adicionar
            const hasModule = user.accessibleModules.some(
              (module) => module.id === moduleRef.id
            );

            if (!hasModule) {
              return {
                ...user,
                accessibleModules: [...user.accessibleModules, moduleRef],
              };
            }
          }
          return user;
        })
      );
    } catch (error) {
      console.warn(
        "[handleSaveUsersToModule] Erro salvando usuarios ao modulo: ",
        error
      );
    } finally {
      setLoadingModules((prev) => ({ ...prev, [moduleRef.id]: false }));
    }
  };

  const memoizedModules = useMemo(
    () =>
      modulesData
        .sort((a, b) => (a.priorityIndex || 0) - (b.priorityIndex || 0))
        .map((module) => {
          if (!module.isActive) {
            return;
          }

          const moduleData = getModuleUsersData(module.id);

          return (
            <ModuleCard
              key={module.id}
              module={module}
              manualUsers={moduleData.manualUsers}
              subsidiaries={moduleData.subsidiaries}
              manualUserCount={moduleData.manualUserCount}
              handleRemoveUser={removeUserFromModule}
              handleAddUser={() => handleAddUser(module)}
              handleRemoveUserModal={() => handleRemoveUser(module)}
              loading={loadingModules[module.id]}
            />
          );
        }),
    [
      modulesData,
      loadingModules,
      getModuleUsersData,
      removeUserFromModule,
      handleAddUser,
      handleRemoveUser,
    ]
  );

  return (
    <div>
      {/* Barra de pesquisa e upload */}
      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      {/* Exibe os módulos e os usuários em cada módulo */}
      <div className="modules-users-container">{memoizedModules}</div>

      {/* Modal para adicionar usuários ao módulo */}
      {isAddingUser && (
        <AddUserModal
          module={selectedModule}
          onClose={() => setIsAddingUser(false)}
          onSave={handleSaveUsersToModule}
          usersData={usersData}
          userRole={userRole}
          userInformations={userInformations}
        />
      )}

      {isRemovingUser && (
        <RemoveUserModal
          module={selectedModule}
          onClose={() => setIsRemovingUser(false)}
          onRemove={handleRemoveUsersFromModule}
          usersData={usersData}
          userRole={userRole}
          userInformations={userInformations}
        />
      )}
    </div>
  );
}

export default UsersSection;
